import { Component, inject } from '@angular/core';
import { FormControl } from '@angular/forms';
import { BehaviorSubject, first, tap } from 'rxjs';
import { AssignmentFormModel } from 'src/app/core/models/project/assignment/assignment-form.model';
import { MinimalProfile } from 'src/app/core/models/user/profile';
import { FileService } from 'src/app/core/services/file.service';

@Component({
    selector: 'app-editable-assignment',
    template: '',
    standalone: false
})
export class EditableAssignmentComponent {
  formModel = inject(AssignmentFormModel);
  protected readonly fileService = inject(FileService);

  public readonly today = new Date();
  public readonly employeeSearchConfig = {
    filterFn: (item: MinimalProfile, term: string): boolean => {
      const searchTerm = term.trim().toLowerCase();

      if (!searchTerm) {
        return true;
      }

      const fullName = (item.fullName ?? '').toLowerCase();
      const email = (item.email ?? '').toLowerCase();
      return fullName.includes(searchTerm) || email.includes(searchTerm);
    },
    formatter: (item: MinimalProfile | null | undefined): string =>
      item?.fullName ?? '',
    compareFn: (item: MinimalProfile, value: MinimalProfile): boolean =>
      item.id === value.id,
  };

  public assignmentEmployee: MinimalProfile | null = null;
  public selectedEmployee: MinimalProfile | null = null;
  public selectedEmployeeControl = new FormControl<string | null>(null);

  public projectEmployees$ = new BehaviorSubject<MinimalProfile[]>([]);
  public assignmentFormModel: AssignmentFormModel;

  // These fields just for ease of use on template
  public titleFormControl: FormControl<string | null>;
  public descriptionFormControl: FormControl<string | null>;
  public deadlineFormControl: FormControl<Date | string | null>;

  public companyId = '';
  public projectId = '';

  constructor() {
    const formModel = this.formModel;

    this.assignmentFormModel = formModel;

    this.titleFormControl = this.assignmentFormModel.form.controls.title;
    this.descriptionFormControl =
      this.assignmentFormModel.form.controls.description;
    this.deadlineFormControl = this.assignmentFormModel.form.controls.deadline;
  }

  public onSelectEmployee(employee: MinimalProfile | null): void {
    if (!employee) {
      this.selectedEmployee = null;
      return;
    }

    this.selectedEmployee = this.projectEmployees$
      .getValue()
      .some((item) => item.id === employee.id)
      ? employee
      : null;
  }

  public addEmployee(employee: MinimalProfile): void {
    const employeeProfile: MinimalProfile = { ...employee, imageUrl: '' };

    this.fileService
      .getEntityWithImageUrl(
        employeeProfile,
        `users/${employeeProfile.id}/avatar${employeeProfile.avatarImageExtension}`,
      )
      .pipe(
        first(),
        tap((profile) => {
          this.selectedEmployee = null;
          this.selectedEmployeeControl.setValue(null);
          const employees = this.getNormalizedEmployeePool(profile);
          this.assignmentEmployee = profile;
          this.projectEmployees$.next(employees);
        }),
      )
      .subscribe();
  }

  public removeEmployee(employeeProfile: MinimalProfile): void {
    this.assignmentEmployee = null;
    this.selectedEmployee = null;
    this.selectedEmployeeControl.setValue(null);
    this.projectEmployees$.next(this.getNormalizedEmployeePool(null, employeeProfile));
  }

  protected resetAssignmentDraft(): void {
    this.assignmentEmployee = null;
    this.selectedEmployee = null;
    this.selectedEmployeeControl.setValue(null);
    this.projectEmployees$.next([]);
    this.assignmentFormModel.form.reset();
  }

  private getNormalizedEmployeePool(
    nextAssignmentEmployee: MinimalProfile | null,
    employeeToRestore?: MinimalProfile,
  ): MinimalProfile[] {
    const employeesById = new Map<string, MinimalProfile>();

    for (const employee of this.projectEmployees$.getValue()) {
      if (employee.id !== nextAssignmentEmployee?.id) {
        employeesById.set(employee.id, employee);
      }
    }

    const previousEmployee = employeeToRestore ?? this.assignmentEmployee;
    if (previousEmployee && previousEmployee.id !== nextAssignmentEmployee?.id) {
      employeesById.set(previousEmployee.id, previousEmployee);
    }

    return [...employeesById.values()].sort((left, right) =>
      (left.fullName ?? '').localeCompare(right.fullName ?? ''),
    );
  }
}
